// Image validation utilities for production security

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
];

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MIN_DIMENSIONS = { width: 200, height: 200 };
export const MAX_DIMENSIONS = { width: 4000, height: 4000 };

export const validateImageFile = (file) => {
  const errors = [];

  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    errors.push('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`Image size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }

  if (file.size < 1024) {
    errors.push('Image file appears to be corrupted or too small');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const errors = [];

      if (width < MIN_DIMENSIONS.width || height < MIN_DIMENSIONS.height) {
        errors.push(`Image must be at least ${MIN_DIMENSIONS.width}x${MIN_DIMENSIONS.height} pixels`);
      }

      if (width > MAX_DIMENSIONS.width || height > MAX_DIMENSIONS.height) {
        errors.push(`Image must be smaller than ${MAX_DIMENSIONS.width}x${MAX_DIMENSIONS.height} pixels`);
      }

      resolve({
        isValid: errors.length === 0,
        errors,
        dimensions: { width, height }
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        errors: ['Unable to process image file'],
        dimensions: null
      });
    };

    img.src = url;
  });
};

export const compressImage = (file, quality = 0.8, maxWidth = 1200, maxHeight = 1200) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((compressedBlob) => {
        const compressedFile = new File([compressedBlob], file.name, {
          type: file.type,
          lastModified: Date.now()
        });
        resolve(compressedFile);
      }, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export const detectInappropriateContent = async (imageUrl) => {
  // This is a placeholder for content moderation
  // In production, you would integrate with:
  // - Google Cloud Vision API
  // - AWS Rekognition
  // - Cloudinary's AI moderation
  // - Azure Content Moderator
  
  try {
    // Example: Check image URL for obvious issues
    const response = await fetch(imageUrl, { method: 'HEAD' });
    if (!response.ok) {
      return { isAppropriate: false, reason: 'Image not accessible' };
    }
    
    // For now, return as appropriate
    // In production, implement actual content detection
    return { isAppropriate: true, reason: null };
  } catch (error) {
    console.error('Content moderation error:', error);
    return { isAppropriate: false, reason: 'Unable to verify content' };
  }
};